/**
 * Painel Berytus › Curadoria: candidatos ao mestre. EANs de NÃO medicamento que os clientes cadastraram e o mestre ainda
 * não tem, ordenados pelo número de contas com o mesmo EAN. O item do cliente já está ativo, com os dados dele (sem
 * inativação, sem teto de PMC, medida padrão nunca bloqueia). A ficha criada aqui só PREENCHE o que o cliente deixou
 * vazio: título, descrição, marca e fotos do cliente continuam valendo. Se a PharmaDB disser que o EAN é medicamento,
 * ele sai daqui e vai para a regra de medicamento (Itens novos).
 */
import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, dateTime, int } from '../api.ts';
import { ErrorBox, Loading, Tag } from '../components/ui.tsx';

interface Candidate { gtin: string; tenants: number; skus: number; qualidoc: boolean; title: string; brand: string | null; titles: string[]; tenantNames: string[]; firstSeen: string;
  pendingFiche: { id: string; title: string } | null }

export function AdminMasterCandidates() {
  const [q, setQ] = useState('');
  const [form, setForm] = useState<{ gtin: string; title: string; brand: string; description: string } | null>(null);
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ['admin', 'candidates', q], queryFn: () => api<{ items: Candidate[] }>(`/admin/curation/candidates?q=${encodeURIComponent(q)}`) });
  const fiche = useMutation({
    mutationFn: (b: NonNullable<typeof form>) => api<{ linked: number }>('/admin/curation/new-items/fiche', { method: 'POST',
      json: { gtin: b.gtin, title: b.title, brand: b.brand || undefined, description: b.description || undefined, isMedicine: false } }),
    onSuccess: (r) => { window.alert(`Ficha criada; ${r.linked} SKU(s) vinculados. Os dados dos clientes não mudam (a ficha só preenche o que estava vazio).`); setForm(null); qc.invalidateQueries({ queryKey: ['admin', 'candidates'] }); },
  });
  return (
    <>
      <div className="page-head">
        <div>
          <h1>Curadoria: candidatos ao mestre</h1>
          <p>Não medicamentos sem ficha, pelo número de contas que cadastraram o mesmo EAN. A ficha criada só preenche campos vazios dos clientes.</p>
        </div>
      </div>
      <div className="toolbar" style={{ marginBottom: 12 }}>
        <input className="input" placeholder="EAN, nome ou conta" value={q} onChange={(e) => setQ(e.target.value)} style={{ maxWidth: 320 }} />
      </div>
      {fiche.error && <ErrorBox error={fiche.error} />}
      {form && (
        <section className="panel" style={{ marginBottom: 12 }}>
          <h3>Criar ficha (não medicamento) · EAN {form.gtin}</h3>
          <div style={{ display: 'grid', gap: 8, gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
            <label>Nome<input className="input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></label>
            <label>Marca<input className="input" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} /></label>
          </div>
          <label style={{ display: 'block', marginTop: 8 }}>Descrição (opcional; só para quem não tem)<textarea className="input" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></label>
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button type="button" className="btn primary" disabled={fiche.isPending || form.title.trim().length < 3} onClick={() => fiche.mutate(form)}>Criar e vincular</button>
            <button type="button" className="btn" onClick={() => setForm(null)}>Cancelar</button>
          </div>
        </section>
      )}
      {list.error ? <ErrorBox error={list.error} /> : !list.data ? <Loading /> : !list.data.items.length ? <div className="empty"><b>Nada aqui</b>Nenhum não medicamento sem ficha.</div> : (
        <section className="panel flush">
          <div className="grid-wrap">
            <table className="grid" style={{ width: '100%', minWidth: 900 }}>
              <thead><tr><th>EAN</th><th>Nome mais usado</th><th className="num">Contas</th><th className="num">SKUs</th><th>Primeiro cadastro</th><th /></tr></thead>
              <tbody>{list.data.items.map((c) => (
                <tr key={c.gtin} style={{ cursor: 'default' }}>
                  <td className="mono">{c.gtin}</td>
                  <td style={{ whiteSpace: 'normal', maxWidth: 340 }}><b>{c.title}</b>{c.brand && <div className="small muted">{c.brand}</div>}
                    {c.titles.length > 1 && <details className="small"><summary>outros nomes</summary>{c.titles.map((t) => <div key={t}>{t}</div>)}</details>}
                    {c.pendingFiche && <div className="small">Ficha em curadoria (PharmaDB): <Link to={`/admin/catalogo/${c.pendingFiche.id}`}>{c.pendingFiche.title}</Link></div>}</td>
                  <td className="num"><b>{int(c.tenants)}</b>{c.qualidoc && <div><Tag tone="info">Qualidoc</Tag></div>}<div className="small muted" title={c.tenantNames.join(', ')}>{c.tenantNames.slice(0, 2).join(', ')}{c.tenantNames.length > 2 ? '…' : ''}</div></td>
                  <td className="num">{int(c.skus)}</td>
                  <td className="small">{dateTime(c.firstSeen)}</td>
                  <td><button type="button" className="btn sm" onClick={() => setForm({ gtin: c.gtin, title: c.title, brand: c.brand ?? '', description: '' })}>Criar ficha</button></td>
                </tr>))}</tbody>
            </table>
          </div>
        </section>
      )}
      <MasterSyncReport />
    </>
  );
}

/** Por conta: itens não medicamento ligados ao mestre, complementados, sincronizados e com campos próprios do cliente. */
function MasterSyncReport() {
  const q = useQuery({ queryKey: ['admin', 'master-sync-report'], queryFn: () => api<{ mode: string; items: { tenantId: string; tenant: string; linked: number; synced: number; complemented: number; ownFields: number; wouldComplement: number }[] }>('/admin/curation/master-sync-report') });
  return (
    <section className="panel flush" style={{ marginTop: 16 }}>
      <div className="panel-head"><h2>Não medicamento no mestre, por conta</h2><span className="small muted">complemento em <b>{q.data?.mode ?? '…'}</b>{q.data?.mode === 'observe' ? ' (só conta o que completaria)' : ''}; sincronizar é escolha do cliente</span></div>
      {q.error ? <ErrorBox error={q.error} /> : !q.data ? <Loading /> : !q.data.items.length ? <div className="empty"><b>Nada ainda</b>Nenhum não medicamento ligado ao mestre.</div> : (
        <table className="simple">
          <thead><tr><th>Conta</th><th className="num">Ligados ao mestre</th><th className="num">Complementados</th><th className="num">Sincronizados</th><th className="num">Com campos próprios</th><th className="num">Completaria (observe)</th></tr></thead>
          <tbody>{q.data.items.map((r) => <tr key={r.tenantId}><td>{r.tenant}</td><td className="num">{int(r.linked)}</td><td className="num">{int(r.complemented)}</td>
            <td className="num">{int(r.synced)}</td><td className="num">{int(r.ownFields)}</td><td className="num">{int(r.wouldComplement)}</td></tr>)}</tbody>
        </table>
      )}
    </section>
  );
}
